import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Skeleton } from "antd";
import { t } from "i18next";

import { useAffirm } from "../../../CustomHooks/useAffirm";
import Loading from "../../loadingScreen/Loading";
import { buildAffirmVcnCheckout } from "../Affirm/affirmCheckout";
import { getTransactionStatus } from "../../../../slices/TransactionIdSlice";

export default function AffirmWalletButton({ handleInitiatePayment, handleInitiatePaymentAnalytics, surchargeAmount = 0 }) {
  const { affirm, ready, error } = useAffirm();
  const dispatch = useDispatch();
  const sessionDetails = useSelector((state) => state.paymentSessionDetails.sessionDetails);
  const updatedShopperDetails = useSelector((state) => state.paymentSessionDetails.updatedShopperDetails);
  const [isProcessing, setIsProcessing] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!ready || !containerRef.current) return;
    // Paint Affirm's pre-styled button into the container. No-op if already
    // drawn; must be re-run on SPA re-mount.
    affirm?.ui?.checkoutButton?.render?.();
  }, [ready, affirm]);

  const handleClick = useCallback(() => {
    if (!ready || !affirm?.checkout) return;

    setIsProcessing(true);
    try {
      const affirmCheckoutData = buildAffirmVcnCheckout(sessionDetails, surchargeAmount, updatedShopperDetails);
      affirm.checkout(affirmCheckoutData);
      affirm.checkout.open_vcn({
        success: async (result) => {
          const checkoutToken = result?.checkout_token;
          if (!checkoutToken) {
            setIsProcessing(false);
            console.error("[Affirm] VCN success missing checkout_token", result);
            return;
          }

          handleInitiatePaymentAnalytics?.("Affirm");
          try {
            const res = await handleInitiatePayment?.("bnpl/affirm", checkoutToken);

            // Retry-limit modal is opened globally by handleInitiatePayment.
            if (res?.retryLimitReached) {
              setIsProcessing(false);
              return;
            }

            if (res?.formActionUrl) {
              window.location.replace(res.formActionUrl);
              return;
            }

            if (res?.data?.transactionId) {
              const txnStatus = res.data?.status?.status;
              if (txnStatus === "Rejected" || txnStatus === "FAILED") {
                setIsProcessing(false);
              }

              dispatch(getTransactionStatus({
                token: sessionDetails?.token,
                transactionId: res.data.transactionId,
              }));
              return;
            }

            setIsProcessing(false);
            handleInitiatePaymentAnalytics?.("Affirm/paymentInitiateError", JSON.stringify(res));
            console.error("[Affirm] payment initiation failed", res);
          } catch (err) {
            setIsProcessing(false);
            handleInitiatePaymentAnalytics?.("Affirm/paymentInitiateError", JSON.stringify(err));
            console.error("[Affirm] payment initiation error", err);
          }
        },
        error: async (result) => {
          console.warn("[Affirm] VCN checkout failed/cancelled", result);
          const errorMsg = result?.reason || result?.message || result?.error_message || "Affirm checkout failed or cancelled";
          handleInitiatePaymentAnalytics?.("Affirm/checkoutError", JSON.stringify(result));
          try {
            await handleInitiatePayment?.("bnpl/affirm", "", errorMsg);
          } catch (err) {
            console.error("[Affirm] failed to report checkout error", err);
          } finally {
            setIsProcessing(false);
          }
        },
        onValidationError: (result) => {
          setIsProcessing(false);
          console.error("[Affirm] VCN checkout validation error", result);
        },
      });
    } catch (err) {
      setIsProcessing(false);
      console.error("[Affirm] failed to open VCN checkout", err);
    }
  }, [ready, affirm, sessionDetails, updatedShopperDetails, surchargeAmount, handleInitiatePayment, handleInitiatePaymentAnalytics, dispatch]);

  if (error) return null;

  return (
    <div className="payment-container affirm-express-cell">
      {isProcessing && <Loading text={t("PLEASE_WAIT_PROCESSING_PAYMENT")} />}

      {!ready && (
        <Skeleton.Button active style={{ width: "100%", height: 40 }} shape="default" />
      )}

      <div
        role="button"
        tabIndex={0}
        aria-label="Pay with Affirm"
        className="affirm-express-btn"
        style={{ display: ready ? "flex" : "none" }}
        onClick={handleClick}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") handleClick();
        }}
      >
        <div
          ref={containerRef}
          className="affirm-checkout-button-container"
          data-page-type="payment"
          data-size="small"
          data-theme="light"
          data-button-text="logo-only"
          data-full-width="true"
        />
      </div>
    </div>
  );
}
